<template>
  <div>
    <div class="wrapper">
      <b-navbar toggleable="lg" type="light">
        <b-navbar-brand :to="logoRoute">
          <img :src="logo" alt="Logo" class="d-inline-block align-top">
        </b-navbar-brand>

        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

        <b-collapse id="nav-collapse" is-nav>
          <b-navbar-nav v-if="activeApp?.name === 'fms'">
            <b-nav-item
              v-for="item in fmsNavItems"
              :key="item.key"
              :to="item.to"
              :href="item.href"
              :class="{ 'selected': $route.name === item.selectedWhen }"
              class="d-flex align-items-center">
              <img :src="iconFor(item)" class="mr-2" />
              <span :class="{ 'pink-text': $route.name === (item.labelActiveWhen || item.selectedWhen) }">{{ item.label }}</span>
            </b-nav-item>
          </b-navbar-nav>

          <b-navbar-nav v-if="activeApp?.name === 'bunkering'">
            <b-nav-item
              v-for="item in bunkeringNavItems"
              :key="item.key"
              :to="item.to"
              :class="{ 'selected': $route.name === item.selectedWhen }"
              class="d-flex align-items-center">
              <img :src="iconFor(item)" class="mr-2" />
              <span :class="{ 'pink-text': $route.name === item.selectedWhen }">{{ item.label }}</span>
            </b-nav-item>
          </b-navbar-nav>

          <b-navbar-nav class="navbar-actions ml-auto d-flex align-items-center">

            <!-- Select Company and App Dropdown -->
            <b-nav-item v-if="hideCompanySelection" v-on:click="openSelectAppModal"
              class="d-flex align-items-center">
              <img :src="icon.IconApp" alt="App Icon" class="mr-2" style="width: 18px; height: 18px;">
              <span>Select Application</span>
            </b-nav-item>
            <b-nav-item-dropdown v-else text="Options" right>
              <template #button-content>
                <img :src="icon.IconSelection" class="mr-2" style="width: 18px; height: 18px;">
                <span>Select App/Company</span>
              </template>
              <b-dropdown-item v-on:click="openSelectAppModal">
                <img :src="icon.IconApp" alt="App Icon" class="mr-2" style="width: 22px; height: 22px;">
                Select Application
              </b-dropdown-item>
              <b-dropdown-item v-on:click="openSelectCompanyModal">
                <img :src="icon.IconCompany" alt="Company Icon" class="mr-2">
                Select Company
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <!--/ Select Company and App Dropdown -->

            <navbar-notifications v-if="canRead('notification_inbox')" />

            <!-- Config Button -->
            <b-nav-item :to="{ name: 'MasterConfiguration' }"
              :class="{ 'selected': $route.name === 'MasterConfiguration' }"
              class="d-flex align-items-center navbar-action-item" v-b-tooltip.hover title="Configuration">
              <i class="ri-settings-3-line" style="font-size: 1.5rem;"
                :class="{ 'pink-text': $route.name === 'MasterConfiguration' }"></i>
            </b-nav-item>

            <!-- dropdown user -->
            <b-nav-item-dropdown class="d-flex align-items-center navbar-action-item" right>
              <!-- Using 'button-content' slot -->
              <template #button-content>
                <img :src="profileImage" class="mr-2" :style="{ width: '25px', height: '25px' }">
                <span class="truncate">{{ userId }}</span>
              </template>
              <b-dropdown-item href="#">Profile</b-dropdown-item>
              <b-dropdown-item @click="handleLogout">Sign Out</b-dropdown-item>
            </b-nav-item-dropdown>
            <!--/ dropdown user -->
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>

      <b-modal id="select-app-modal" title="Select App" centered hide-footer>
        <app-active-selection hideHeader=true />
      </b-modal>

      <b-modal id="select-company-modal" title="Select Company" centered hide-footer>
        <company-active-selection hideHeader=true disableRedirection=true />
      </b-modal>

      <div>
        <transition name="router-anim">
          <router-view :key="$store.state.remountKey" />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import logo from '@/assets/images/Mantraz-logo.svg';
import profileImage from '@/assets/images/user/default-user.png';
// import { sofbox } from '@/config/pluginInit';

import AppActiveSelection from '@/views/auth/AppActiveSelection';
import CompanyActiveSelection from '@/views/auth/CompanyActiveSelection';
import NavbarNotifications from '@/components/navbar/NavbarNotifications';

import icon from './navIcons';
import { FMS_NAV_ITEMS, BUNKERING_NAV_ITEMS } from './navConfig';

export default {
  name: 'AuthorizedLayout',
  components: {
    AppActiveSelection,
    CompanyActiveSelection,
    NavbarNotifications,
  },
  computed: {
    ...mapState(['remountKey']),
    activeCompany() {
      return this.$store.getters['auth/activeCompany'];
    },
    userAccess() {
      const access = this.$store.getters['auth/userAccess'];
      return access;
    },
    userId() {
      return this.$store.getters['auth/userId'];
    },
    activeApp() {
      return this.$store.getters['auth/activeApp'];
    },
    logoRoute() {
      if (this.activeApp?.name === 'bunkering') {
        return { name: 'BunkerDashboard' };
      }

      return { name: 'Home' };
    },
    fmsNavItems() {
      return FMS_NAV_ITEMS.filter((item) => !item.requires || item.requires(this));
    },
    bunkeringNavItems() {
      return BUNKERING_NAV_ITEMS.filter((item) => !item.requires || item.requires(this));
    },
  },
  data() {
    return {
      hideCompanySelection: false,
      logo,
      profileImage,
      icon,
      availableCompany: [],
    };
  },
  mounted() {
    // sofbox.mainIndex();
    this.availableCompany = this.$store.getters['auth/availableCompany'];

    const availableCompaniesCount = Object.values(this.$store.getters['auth/availableCompany']).length;
    if (availableCompaniesCount === 1) this.hideCompanySelection = true;
  },
  methods: {
    handleLogout() {
      this.$store.dispatch('auth/logout');
      window.location.href = '/auth/login';
    },
    openSelectAppModal() {
      this.$bvModal.show('select-app-modal');
    },
    openSelectCompanyModal() {
      this.$bvModal.show('select-company-modal');
    },
    iconFor(item) {
      if (item.activeIcon && this.$route.name === item.selectedWhen) return this.icon[item.activeIcon];
      return this.icon[item.icon];
    },
  },
};
</script>
<style src="./AuthorizedLayout.css"></style>
